import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { WorkLoadsCalendar } from "@planzone/dto";

import { Views } from "../../api/views";
import { Context } from "../../context";
import { useScrollPos } from "../../scrollPosContext";
import { Props } from "./types";
import styles from "./styles.scss";

const DAY_MS = 1000 * 60 * 60 * 24;

function getWeekLength(
  { week }: WorkLoadsCalendar[number]["weeks"][number],
  [min, max]: [DateTime, DateTime],
) {
  const from = DateTime.fromISO(week.from, { zone: "utc" });
  const to = DateTime.fromISO(week.to, { zone: "utc" });
  const start = from > min ? from : min;
  const end = to < max ? to : max;

  return (end.toMillis() - start.toMillis()) / DAY_MS + 1;
}

function toFixed(nbr: number) {
  return typeof nbr !== "undefined"
    ? Math.round(nbr) === nbr
      ? nbr
      : nbr.toFixed(1)
    : "";
}
function getMonthLength(
  { month }: WorkLoadsCalendar[number]["months"][number],
  [min, max]: [DateTime, DateTime],
) {
  const from = DateTime.fromISO(month.from, { zone: "utc" });
  const to = DateTime.fromISO(month.to, { zone: "utc" });
  const start = from > min ? from : min;
  const end = to < max ? to : max;

  return Math.round((end.toMillis() - start.toMillis()) / DAY_MS) + 1;
}

function calculateDaysBetweenDates(startDate: DateTime, endDate: DateTime) {
  const daysDifference = (endDate.toMillis() - startDate.toMillis()) / DAY_MS;

  return Math.round(daysDifference);
}

function Calendar({ workLoads, dates }: Props) {
  const ref = React.useRef<HTMLDivElement>(null);
  const { cell, currentView } = React.useContext(Context);
  const startIndex = useScrollPos();
  const endIndex = startIndex + Math.round(screen.width / cell.width);
  const datesChunk = dates.slice(startIndex, endIndex);

  return (
    <div ref={ref} className={styles.calendar}>
      <div
        className={styles.main}
        style={{
          width: dates.length * cell.width,
          height: cell.height * workLoads.length,
          marginLeft:
            currentView !== Views.MONTHLY && currentView !== Views.YEARLY
              ? startIndex * cell.width
              : undefined,
        }}
      >
        {currentView === Views.MONTHLY ? (
          <div className={styles.workLoads} style={{}}>
            {workLoads
              .map((workLoad) => ({
                uuid: workLoad.uuid,
                weeks: workLoad.weeks,
              }))
              .map((workLoad) => (
                <ul key={workLoad.uuid} className={styles.workLoad}>
                  {workLoad.weeks.map((week, index, all) => {
                    const isOutsideProjectScope =
                      DateTime.fromISO(week.week.from, { zone: "utc" }) <
                      dates[0];

                    return isOutsideProjectScope ? (
                      <li
                        style={{
                          minWidth:
                            cell.width *
                            (calculateDaysBetweenDates(
                              dates[0],
                              DateTime.fromISO(week.week.to, { zone: "utc" }),
                            ) +
                              1),
                          minHeight: cell.height,
                          width: 0,
                          height: 0,
                        }}
                      />
                    ) : (
                      <li
                        key={index}
                        className={classNames(styles.day, {
                          [styles.good]: week.load <= 100,
                          [styles.bad]: week.load > 100 && week.load < 130,
                          [styles.worse]: week.load >= 130,
                          [styles.restDay]: !week.workedWeek,
                        })}
                        style={{
                          minWidth:
                            cell.width *
                            getWeekLength(week, [
                              dates[0],
                              dates[dates.length - 1],
                            ]),
                          minHeight: cell.height,
                          width: 0,
                          height: 0,
                        }}
                      >
                        <p>
                          {week.workedWeek ? `${toFixed(week.capacity)}` : ""}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              ))}
          </div>
        ) : currentView === Views.YEARLY ? (
          <div className={styles.workLoads} style={{}}>
            {workLoads
              .map((workLoad) => ({
                uuid: workLoad.uuid,
                months: workLoad.months,
              }))
              .map((workLoad) => (
                <ul key={workLoad.uuid} className={styles.workLoad}>
                  {workLoad.months.map((month, index, all) => (
                    <li
                      key={index}
                      className={classNames(styles.day, {
                        [styles.good]: month.load <= 100,
                        [styles.bad]: month.load > 100 && month.load < 130,
                        [styles.worse]: month.load >= 130,
                        [styles.restDay]: !month.workedMonth,
                      })}
                      style={{
                        minWidth:
                          cell.width *
                          getMonthLength(month, [
                            dates[0],
                            dates[dates.length - 1],
                          ]),
                        minHeight: cell.height,
                        width: 0,
                        height: 0,
                      }}
                    >
                      <p>
                        {month.workedMonth ? `${toFixed(month.capacity)}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              ))}
          </div>
        ) : (
          <div className={styles.workLoads} style={{}}>
            {workLoads
              .map((workLoad) => ({
                uuid: workLoad.uuid,
                days: new Map(workLoad.days.map((day) => [day.date, day])),
              }))
              .map((workLoad) => (
                <ul key={workLoad.uuid} className={styles.workLoad}>
                  {datesChunk
                    .map((date, index) => ({
                      index,
                      day: workLoad.days.get(date.toFormat("yyyy-MM-dd")),
                    }))
                    .filter((day) => day.day !== null && day.day !== undefined)
                    .map((day) => (
                      <li
                        key={day.index}
                        className={classNames(styles.day, {
                          [styles.good]: day.day ? day.day.load <= 100 : false,
                          [styles.bad]: day.day
                            ? day.day.load > 100 && day.day.load < 130
                            : false,
                          [styles.worse]: day.day ? day.day.load >= 130 : false,
                          [styles.restDay]: day.day && !day.day.workedDay,
                        })}
                        style={{
                          minWidth: cell.width,
                          minHeight: cell.height,
                          width: 0,
                          height: 0,
                        }}
                      >
                        <p>
                          {day.day && day.day.workedDay
                            ? `${toFixed(day.day.capacity)}`
                            : ""}
                        </p>
                      </li>
                    ))}
                </ul>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { Calendar };
